import type { AnySceneObjectT } from "tweenly"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import LockIcon from "@mui/icons-material/Lock"
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOff"
import { useTheme } from "@mui/material"
import Grid from "@mui/material/Grid"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import { t } from "i18next"
import useMultiselectObject from "../../../../hooks/editor/useMultiselectObject"
import { useAppDispatch, useAppSelector } from "../../../../hooks/useRedux"
import { useMasterTimeline } from "../../../../hooks/useMasterTimeline"
import { selectIsObjectInSelection } from "../../../../store/slices/activeObject.slice"
import { selectObjectsRangeAction } from "../../../../store/slices/objects.slice/object/selectionActions"
import { TIMELINE_ROW } from "../../../../style/sizing"
import ObjectTitle from "../../ObjectsTree/ObjectTitle"
import TimelineTimeStepWrapper from "./TimelineRow/TimelineTimeStepWrapper"
import TimelineTweenWrapper from "./TimelineRow/TimelineTweenWrapper"

/**
 * How far below a name cell its background reaches — past the widest gap between two rows —
 * in steps shorter than the cell, so the copies join up (see the names cell below).
 */
const NAME_GAP_COVER_STEPS = [8, 16, 24, 32]

interface Props {
    object: AnySceneObjectT
    timelineRowWidth: number
    animationId: string | null
    isMainRow: boolean
    isHidingObject: boolean
    isLockingObject: boolean
    isExpanded: boolean
    handleCollapse: () => void
    handleExpand: () => void
}

const TimelineRow = ({
    object,
    animationId,
    isMainRow,
    timelineRowWidth,
    isHidingObject,
    isLockingObject,
    isExpanded,
    handleCollapse,
    handleExpand,
}: Props) => {
    const theme = useTheme()
    const { masterTimelineDuration } = useMasterTimeline()
    const duration: number = masterTimelineDuration
    const tweensPerSec: number = 1
    const timeStepsDensity: number =
        Math.ceil(duration * tweensPerSec) > 0 ? Math.ceil(duration * tweensPerSec) : 1
    const tweenWidth: number = duration > 0 ? timelineRowWidth / duration : timelineRowWidth // width per sec
    const timeStepWidth: number = tweenWidth / tweensPerSec - 2
    const timeStepX: number = tweenWidth / tweensPerSec

    const dispatch = useAppDispatch()
    const { handleMultiselectObject } = useMultiselectObject(object)
    // one boolean subscription instead of the two wrapper hooks, which measured the DOM for a
    // bounding box this row never uses and re-rendered every row on any selection change
    const isActive = useAppSelector((state) => selectIsObjectInSelection(state, object.id))

    /**
     * Same split as the objects tree — shift spans a range, ctrl/cmd adds one — but over
     * the TIMELINE's own list of rows, which is not the tree's: an object without
     * animations has no row here at all. See `flattenTimelineObjectIds`.
     */
    const handleSelectObject = (event: React.MouseEvent) => {
        if (event.shiftKey) {
            dispatch(selectObjectsRangeAction(object.id, "timeline"))
            return
        }
        handleMultiselectObject(event.metaKey || event.ctrlKey)
    }

    return (
        <Grid container direction="row" alignItems="center" wrap="nowrap">
            <Grid
                container
                direction="row"
                alignItems="center"
                sx={{
                    zIndex: 50,
                    paddingLeft: "10px",
                    left: "0px",
                    position: "sticky",
                    width: TIMELINE_ROW.headingWidth + "px",
                    height: TIMELINE_ROW.height + "px",
                    marginBottom: TIMELINE_ROW.paddingBottom + "px",
                    background: theme.palette.grey[800],
                    /**
                     * The gap down to the next row is covered too: the column scrolls the
                     * playhead under it, and through the gaps between rows (and between
                     * object groups) it showed as red dashes. Copies of the cell stepped down
                     * as shadows — unlike a box, a shadow adds nothing to what scrolls — and
                     * the next row's name is drawn later, over whatever of them reaches it.
                     */
                    boxShadow: NAME_GAP_COVER_STEPS.map(
                        (offset) => `0 ${offset}px 0 0 ${theme.palette.grey[800]}`
                    ).join(", "),
                }}
            >
                <Grid item md={2}>
                    {isMainRow &&
                        (isExpanded && Object.values(object.animations).length >= 1 ? (
                            <Tooltip title={!object.childIds.length ? "" : t("editor:collapse")}>
                                <span>
                                    <IconButton
                                        onClick={handleCollapse}
                                        disabled={Object.values(object.animations).length === 1}
                                    >
                                        <KeyboardArrowDownIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        ) : (
                            <Tooltip title={t("editor:expand")}>
                                <IconButton onClick={handleExpand}>
                                    <KeyboardArrowRightIcon />
                                </IconButton>
                            </Tooltip>
                        ))}
                </Grid>
                <Grid item md={6} mr={1}>
                    <ObjectTitle
                        title={isMainRow ? object.title : ""}
                        isActive={isActive}
                        handleSelectObject={handleSelectObject}
                    />
                </Grid>
                <Grid item md={1} mr={1}>
                    {isLockingObject && isMainRow ? (
                        <Tooltip title={t("editor:locked")}>
                            <span>
                                <IconButton disabled={true}>
                                    <LockIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : (
                        <></>
                    )}
                </Grid>
                <Grid item md={1}>
                    {isHidingObject && isMainRow ? (
                        <Tooltip title={t("editor:hidden")}>
                            <span>
                                <IconButton disabled={true}>
                                    <VisibilityOffRoundedIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : (
                        <></>
                    )}
                </Grid>
            </Grid>

            {/* tweeny | animation  */}
            <Grid
                sx={{
                    position: "relative",
                    width: timelineRowWidth > 1000 ? 1000 : timelineRowWidth + "px", //100%
                    height: TIMELINE_ROW.height + "px",
                    overflow: "visible",
                }}
            >
                {timeStepsDensity &&
                    [...Array(timeStepsDensity)].map((_, index) => {
                        return (
                            <TimelineTimeStepWrapper
                                objectId={object.id}
                                key={index}
                                index={index}
                                TimelineTimeStepWidth={
                                    [...Array(timeStepsDensity)].length - index === 0
                                        ? tweenWidth
                                        : timeStepWidth
                                }
                                timelineStepX={timeStepX}
                            />
                        )
                    })}
                {!animationId &&
                    Object.keys(object.animations).map((rowAnimationId: string, index: number) => {
                        const animation = object.animations[rowAnimationId]
                        return (
                            <TimelineTweenWrapper
                                key={index}
                                index={index}
                                objectId={object.id}
                                animationId={rowAnimationId}
                                TimelineTweenWidth={tweenWidth}
                                delay={Number(animation?.delay) || 0}
                                duration={Number(animation?.duration) || 0}
                            />
                        )
                    })}
                {animationId && (
                    <TimelineTweenWrapper
                        index={0}
                        objectId={object.id}
                        animationId={animationId}
                        TimelineTweenWidth={tweenWidth}
                        delay={Number(object.animations[animationId]?.delay) || 0}
                        duration={Number(object.animations[animationId]?.duration) || 0}
                    />
                )}
            </Grid>
        </Grid>
    )
}

export default TimelineRow
